import z from 'zod';

const AddressSchema = z.object({
    name: z.string().catch(''),
    street: z.string().catch(''),
    number: z.string().catch(''),
    postalCode: z.string().catch(''),
    city: z.string().catch(''),
    state: z.string().catch(''),
    country: z.string().catch(''),
});

const ContactInfoSchema = z.object({
    type: z.enum(['phone', 'email', 'address']),
    purpose: z.string().catch(''),
    value: z.string(),
});

export const FirecrawlLocationSchema = z.object({
    name: z.string().catch(''),
    address: AddressSchema,
    contactInfo: z.array(ContactInfoSchema).catch([]),
    isHeadquarter: z.boolean().catch(false),
});

export type FirecrawlLocation = z.infer<typeof FirecrawlLocationSchema>;

const GeneralInfoSchema = z.discriminatedUnion('name', [
    z.object({
        name: z.literal('companySize'),
        description: z.literal('The number of employees in the company.'),
        value: z.string(),
    }),
    z.object({
        name: z.literal('systemSize'),
        description: z.literal('The typical size or range of power system projects undertaken by the company.'),
        value: z.string(),
    }),
    z.object({
        name: z.literal('yearsInBusiness'),
        description: z.literal('Number of years the company has been in business.'),
        value: z.string(),
    }),
    z.object({
        name: z.literal('foundedYear'),
        description: z.literal('The year the company was founded.'),
        value: z.string(),
    }),
    z.object({
        name: z.literal('activeProjects'),
        description: z.literal('Active projects currently being managed by the company.'),
        value: z.string(),
    }),
]);
export const FirecrawlCompanyInfoSchema = z.object({
    name: z.string().nullish().describe('The official name of the company.'),
    description: z.string().nullish().describe('A paragraph description of what the company does.'),
    logo: z.string().describe('URL of the company logo.').nullish(),
    socialMediaLinks: z
        .object({
            linkedin: z.string().describe('URL').nullish(),
            twitter: z.string().describe('URL').nullish(),
            facebook: z.string().describe('URL').nullish(),
            youtube: z.string().describe('URL').nullish(),
        })
        .catch({}),
    locations: z.array(FirecrawlLocationSchema).catch([]),
    missionStatement: z.string().nullish(),
    generalInfo: z.array(GeneralInfoSchema).catch([]),
    partners: z.array(z.string()).describe('Company names of partners and collaborators.').catch([]),
});
export type FirecrawlCompanyInfo = z.infer<typeof FirecrawlCompanyInfoSchema>;

export const FirecrawlCaseStudyInfoSchema = z
    .object({
        url: z.string().url().nullish(),
        title: z.string().nullish(),
        shortTitle: z.string().nullish(),
        description: z.string().nullish(),
        shortDescription: z.string().nullish(),
        heroImage: z.string().url().nullish(),
        images: z.array(z.string().url()).catch([]),
        attributes: z
            .array(
                z.object({
                    name: z.enum(['location', 'systemSize', 'date']),
                    value: z.string(),
                }),
            )
            .describe('Quantitative attributes about the case study.')
            .catch([]),
    })
    .passthrough();
export type FirecrawlCaseStudyInfo = z.infer<typeof FirecrawlCaseStudyInfoSchema>;

export const FirecrawlServiceInfoSchema = z
    .object({
        heroImage: z.string().url().nullish(),
        title: z.string().nullish(),
        description: z.string().nullish(),
        shortDescription: z.string().nullish(),
        capabilities: z
            .array(
                z.object({
                    title: z.string().nullish(),
                    description: z.string().nullish(),
                }),
            )
            .catch([]),
        tags: z
            .array(z.string())
            .describe('Tags are used to filter services. Each tag should be a short, lowercase keyword.')
            .catch([]),
        keyDifferentiators: z
            .array(z.string())
            .describe('List of short statement that differentiates the service')
            .catch([]),
        isService: z.boolean().optional().nullable().describe('Indicates if the entry describes a service offering'),
        isPowerSystemsRelated: z
            .boolean()
            .optional()
            .nullable()
            .describe('Indicates if the service is related to power systems and energy industry'),
    })
    .passthrough();
export type FirecrawlServiceInfo = z.infer<typeof FirecrawlServiceInfoSchema>;

export const FirecrawlSiteMapSchema = z.object({
    same_origins: z.array(z.string()).describe('Allowed hostnames that have the same origin as the base_url'),
    services_urls: z
        .array(z.string().url().startsWith('http'))
        .describe('URLs related to services offered by the company'),
    products_urls: z
        .array(z.string().url().startsWith('http'))
        .describe('URLs related to products offered by the company'),
    product_series_urls: z
        .array(z.string().url().startsWith('http'))
        .describe('URLs related to product series offered by the company'),
    projects_urls: z
        .array(z.string().url().startsWith('http'))
        .describe('URLs related to projects undertaken by the company'),
    locations_urls: z.array(z.string().url().startsWith('http')).describe('URLs related to locations of the company'),
    company_info_urls: z
        .array(z.string().url().startsWith('http'))
        .describe('URLs related to general company information'),
    exclude_urls: z.array(z.string()).describe('URLs to be excluded from processing'),
    explore_further_urls: z.array(z.string()).describe('URLs that need further exploration to classify'),
});

export const ApplicationTagsResponseSchema = z.object({
    candidateApplicationTags: z
        .array(z.string())
        .describe('Initial list of application tags extracted from the content'),
    finalApplicationTags: z
        .array(z.string())
        .describe('Refined list of application tags after applying validation rules'),
});
export type ApplicationTagsResponse = z.infer<typeof ApplicationTagsResponseSchema>;

export const defaultSiteMap: FirecrawlSiteMap = {
    same_origins: [],
    services_urls: [],
    products_urls: [],
    product_series_urls: [],
    projects_urls: [],
    locations_urls: [],
    company_info_urls: [],
    exclude_urls: [],
    explore_further_urls: [],
};
export type FirecrawlSiteMap = z.infer<typeof FirecrawlSiteMapSchema>;

export enum FirecrawlType {
    Root = 'root',
    Services = 'services',
    Products = 'products',
    ProductSeries = 'productSeries',
    Projects = 'projects',
    Locations = 'locations',
    CompanyInfo = 'companyInfo',
}

export enum CompanyRelevanceType {
    NotRelevant = 'notRelevant',
    NeedsReview = 'needsReview',
    GoodCandidate = 'goodCandidate',
}

export const CompanyRelevanceTypeSchema = z.nativeEnum(CompanyRelevanceType);

export type CompanyRelevanceResult = {
    companyImportEvaluation: string;
    relevance: CompanyRelevanceType;
};

const CriterionItem = z.object({
    criterion: z.enum([
        'Explicit Mention of Power or Energy Sector',
        'Relevance to Industry Components or Services',
        'Overall Industry Fit',
        'Ambiguity Level',
    ]),
    match: z.boolean(),
    rationale: z.string(),
});

export const CompanyIndustryRelevance = z.object({
    criteriaEvaluations: z.array(CriterionItem),
    bucket: z.nativeEnum(CompanyRelevanceType),
});

export type CompanyIndustryRelevanceType = z.infer<typeof CompanyIndustryRelevance>;
